import { show } from '../components/core/Modal'
import { stripPathUuids } from './paywall-route-uuids'

// Wraps the raw `show('upgradeYourPlan', ...)` call so a call site can
// declare *why* it is opening the paywall in one place, instead of building
// the modalData object shape by hand at every gate:
//
//   showPaywall('pro', { paywallType: 'limit', featureName: 'aiVideo' })
//
// `trigger` names the control that opened the paywall. feature_name answers
// *what* was gated; trigger answers *which button* gated it, which
// feature_name cannot -- budgeting is reachable from the Tools menu, a project
// hub card and a route guard, and all three produce otherwise identical rows.
//
// It is stamped into the free-form `props` JSONB rather than given a column of
// its own. That was a deliberate trade: no migration, but two consequences
// worth knowing. Nothing validates the value, so keep to the vocabulary below
// rather than inventing a synonym -- a typo becomes a silent extra bucket. And
// the whole props blob is discarded server-side if it ever exceeds 4KB
// (MAX_PROPS_BYTES), which would take the trigger with it; the values here are
// short constants, so that is headroom, not a limit to design against.
//
// Name the CONTROL, in snake_case -- never a category. A trigger has to be
// specific enough that two different buttons can never produce the same row.
// `feature_action` was tried as a catch-all and was useless in exactly the way
// that matters: Scene Workflow and Automatic Storyboard sit on the same screen,
// gate the same feature and hit the same path, so they were indistinguishable.
// If one handler serves several controls, take the trigger as an argument (see
// Scheduling's MainHeader `priceUpdate`) rather than labelling them alike.
//
// In use today:
//   tools_menu               editor header Tools dropdown
//   dashboard_menu           dashboard left-hand menu
//   project_hub_card         feature card on the project hub
//   left_nav                 entry in the persistent left navigation
//                            (ProjectNavigation) -- distinct from the hub
//                            cards, which is what it used to be mislabelled as
//   route_guard              redirect on entering a gated route, no click
//   model_selector           agent control panel's model picker
//   navigator_agent          navigator agent's upgrade_plan command
//   auto_prompt              opens itself, not from a click
//   upsell_alert             an inline upgrade banner's button
//   feature_gate:<key>       FeatureValidation, qualified by entitlement key
//                            (or by trackAs, when the call site sets one --
//                            see FeatureValidation's trackAs prop)
//   scene_workflow, automatic_storyboard, generate_storyboard,
//   add_storyboard_shape, submit_table_read, auto_select_voices, ai_sorting,
//   time_config_upsell, scheduling_settings, duration_configs, full_report,
//   image_generation_limit, autofill_from_script
//
// `targetPath` (optional) is the other half of the "which screen" story.
// `path` (resolved centrally in paywall-analytics.js, not passed here) is
// where the user WAS standing -- the origin. It is accurate but reads as
// wrong on a row like a Budgeting gate fired from the left nav while the
// user was on the storyboard: `path` says storyboard, and the row looks like
// a storyboard event. `targetPath` is where they were BLOCKED FROM GOING --
// the destination -- and is threaded into `props.target_path` instead of
// replacing `path`, because origin is still genuinely useful context and a
// paywall on an in-place control (a model picker, a scene workflow step) has
// no destination at all. Only pass it when the call site actually has a
// destination URL in scope; an absent target_path is correct for those, a
// guessed one is worse than nothing. Stripped of uuids the same way `path`
// is server-side -- see stripPathUuids in paywall-route-uuids.js -- so it
// stays low-cardinality and groupable instead of one value per project.
//
// `vars` (optional) fills the {placeholders} in the copy matrix -- the
// runtime numbers only the call site knows, e.g. { used, limit } on a credit
// or image wall. Every matrix string that takes one also declares a default
// (see paywall-copy.js), so passing nothing is a supported case, not a bug:
// the string still reads correctly with the spec's numbers. It is copy input
// only and is deliberately not forwarded to analytics.
//
// UpgradePlanModal still accepts a bare plan string too -- ~80 existing call
// sites pass one and that dual form must keep working exactly as-is (see its
// own comment). This wrapper always produces the object form, which is a
// superset of the bare string: every field UpgradePlanModal reads today
// (type, closable, onFreeButtonClicked, handleOnClose, withFreeOption,
// paywallType, surface) plus featureName, threaded through to
// usePaywallTracking so paywall_impression/paywall_cta_click rows carry the
// feature the call site actually knows, instead of falling back to whatever
// the route resolver guesses (or NULL, on routes it doesn't cover at all --
// the project hub chief among them).
const showPaywall = (planType, context = {}) => {
  const {
    paywallType,
    featureName,
    trigger,
    vars,
    agent,
    model,
    targetPath,
    closable,
    onFreeButtonClicked,
    handleOnClose,
    withFreeOption,
    surface
  } = context

  return show('upgradeYourPlan', {
    type: planType,
    closable,
    onFreeButtonClicked,
    handleOnClose,
    withFreeOption,
    paywallType,
    featureName,
    trigger,
    agent,
    model,
    targetPath: targetPath ? stripPathUuids(targetPath) : undefined,
    surface,
    vars
  })
}

export default showPaywall
